import React, { FunctionComponent, useState, useEffect } from 'react';
import { Header, Table } from 'semantic-ui-react';
import withStyles from 'react-jss';
import classnames from 'classnames';
import {
  map,
  prop,
  path,
  pathOr,
  sort,
  ascend,
  addIndex,
  isEmpty,
  isNil,
} from 'ramda';

import { ClassProp, Nullable } from 'helpers/types';
import { DateRange } from 'components/DatePicker/DatePicker.helpers';
import {
  OfferStatus,
  Offer,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import StatusPill from './components/StatusPill';
import MoreInfo from './components/MoreInfo';
import OfferActions from './components/OfferActions';
import MarketPlacesPopUp from './components/MarketPlacesPopUp';
import OfferTypeTxt from './components/OfferType';
import ActiveDates from './components/ActiveDates';

import styles from './OffersTable.styles';

type Direction = 'ascending' | 'descending';

type TableLayout = {
  label: string;
  source: Array<number | string>;
  sortable: boolean;
  Component: any | null;
  options?: Record<string, any>;
  width?: 'one' | 'two' | 'three' | 'four' | 'five';
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  data: Offer[];
  classes: Styles;
  className?: string;
};

export type ColumnSort = {
  column?: string;
  direction: Direction;
};

type RowClicked = {
  showDetails: boolean;
  id: Nullable<string>;
};

const mapIndexed = addIndex<TableLayout>(map);

const sorterAsce = (offers: Offer[], fieldName: string) =>
  sort(ascend(prop(fieldName)), offers as Offer[]);

const OFFER_TABLE_LAYOUT: TableLayout[] = [
  {
    label: 'Name',
    source: ['offerName'],
    sortable: true,
    Component: null,
    width: 'two',
  },
  {
    label: 'Offer Source',
    source: ['segments', 0, 'marketplaces'],
    sortable: false,
    Component: MarketPlacesPopUp,
    width: 'two',
  },
  {
    label: 'Status',
    source: ['offerStatus'],
    sortable: true,
    width: 'one',
    Component: StatusPill,
  },
  {
    label: 'Active Dates',
    source: ['offerEndDate'],
    sortable: true,
    Component: ActiveDates,
    options: {
      class: 'noPadding',
    },
  },
  {
    label: 'Campaign ID',
    source: ['merchantServiceCampaignUuid'],
    sortable: true,
    Component: null,
  },
  {
    label: 'Type',
    source: ['offerType'],
    sortable: true,
    width: 'one',
    Component: OfferTypeTxt,
  },
  {
    label: 'Actions',
    source: [],
    sortable: false,
    width: 'two',
    Component: OfferActions,
  },
];

const OffersTable: FunctionComponent<Props> = ({
  data,
  classes,
  className,
}) => {
  const [columnSort, setColumnSort] = useState<ColumnSort>({
    column: undefined,
    direction: 'ascending',
  });

  const [rowClicked, setRowClicked] = useState<RowClicked>({
    showDetails: false,
    id: null,
  });

  const [brandOffers, setBrandOffers] = useState<Offer[]>([]);

  useEffect(() => {
    const currentDate = new Date();
    const offersWithStatus = data
      .filter(
        (offer) =>
          offer.offerStartDate !== null &&
          !offer.offerDeleted &&
          !offer.offerDraft,
      )
      .map((offer) => {
        const offerSchedule = (offer.offerSchedule ?? []).map((dateRange) => {
          return {
            from: new Date(dateRange.startDate),
            to: dateRange?.endDate ? new Date(dateRange.endDate) : new Date(),
          } as DateRange;
        });

        // An Active Offer is one which has at least one offer with a future end date
        if (
          offerSchedule.some(
            (offerDate) => offerDate.to && offerDate.to >= currentDate,
          )
        ) {
          offer.offerStatus = OfferStatus.Active;
        } else {
          // if in the future
          if (
            offerSchedule.some(
              (offerDate) =>
                offerDate.to &&
                offerDate.to >= currentDate &&
                offerDate.from &&
                offerDate.from > currentDate,
            )
          ) {
            offer.offerStatus = OfferStatus.Future;
          } else {
            // has completed
            offer.offerStatus = OfferStatus.Completed;
          }
        }

        return offer;
      });

    offersWithStatus.sort((a, b) => {
      if (a.offerStatus === b.offerStatus) {
        return a.offerStartDate < b.offerStartDate ? 1 : -1;
      }
      return a.offerStatus < b.offerStatus ? 1 : -1;
    });

    setBrandOffers(offersWithStatus);
  }, [data]);

  const handleSort = (clickedColumn: string) => () => {
    let sortedOffers: Offer[] = [];
    const sortDirection = columnSort.direction;
    if (columnSort.column !== clickedColumn) {
      sortedOffers = sorterAsce(brandOffers, clickedColumn);
    } else {
      sortedOffers = brandOffers.reverse();
    }
    setColumnSort({
      column: clickedColumn,
      direction: sortDirection === 'ascending' ? 'descending' : 'ascending',
    });
    setBrandOffers(sortedOffers);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const offerId: Nullable<string> =
      path(['event', 'currentTarget', 'dataset', 'offerid'], event) ||
      event.currentTarget.getAttribute('data-offerid');

    if (offerId === rowClicked.id) {
      setRowClicked({
        showDetails: false,
        id: null,
      });
      return false;
    }

    setRowClicked({
      id: offerId,
      showDetails: true,
    });
  };

  const renderTableHeader = (columnSort: ColumnSort, classes: Styles) => (
    <Table.Header>
      <Table.Row>
        {mapIndexed(renderHeaderCell(columnSort, classes), OFFER_TABLE_LAYOUT)}
      </Table.Row>
    </Table.Header>
  );

  const renderHeaderCell = (columnSort: ColumnSort, classes: Styles) => (
    { label, sortable, source, width }: TableLayout,
    idx: number,
  ) => {
    if (sortable) {
      return (
        <Table.HeaderCell
          width={width}
          className={`${classes.tableHeader} wide`}
          sorted={
            columnSort.column === source[0].toString()
              ? columnSort.direction
              : undefined
          }
          onClick={handleSort(source[0].toString())}
          key={`${source.length > 0 && source[0].toString() + idx}`}
        >
          {label}
        </Table.HeaderCell>
      );
    }

    return (
      <Table.HeaderCell
        width={width}
        className={classnames('wide', classes.tableHeader)}
        key={`${source.length > 0 && source[0].toString() + idx}`}
      >
        {label}
      </Table.HeaderCell>
    );
  };

  const renderBodyCell = (record: Offer) => (
    { source, Component, options }: TableLayout,
    idx: number,
  ) => {
    let fieldValue = pathOr('N/A', source, record);

    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    }

    return (
      <Table.Cell
        key={`${source.length > 0 && source[0].toString() + idx}`}
        className={pathOr('', [options?.class], classes)}
      >
        {Component ? (
          <Component
            fieldValue={fieldValue}
            classes={classes}
            record={record}
            options={options}
            source={source}
          />
        ) : (
          <span className={classes.capitalize}>{fieldValue}</span>
        )}
      </Table.Cell>
    );
  };

  const renderTableRow = (classes: Styles) => (brands: Offer) => {
    const { offerUuid: id } = brands;

    return (
      <React.Fragment key={id}>
        <Table.Row
          data-offerid={id}
          data-merchantid={id}
          onClick={handleRowClick}
          className={classnames(classes.clickable, {
            [classes.supplementalRow]: id === rowClicked.id,
          })}
        >
          {mapIndexed(renderBodyCell(brands), OFFER_TABLE_LAYOUT)}
        </Table.Row>

        {rowClicked.showDetails && id === rowClicked.id && (
          <MoreInfo brands={brands} />
        )}
      </React.Fragment>
    );
  };

  if (isEmpty(brandOffers) || isNil(brandOffers)) {
    return (
      <Table padded={true} className={classnames(className, classes.container)}>
        {renderTableHeader(columnSort, classes)}
        <Table.Body>
          <Table.Row className={classes.centeredRow}>
            <Table.Cell colSpan={8}>
              <Header
                as="h2"
                className={classes.header}
                content="No Offers found!"
              />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    );
  }

  return (
    <Table
      className={classnames(className, classes.container)}
      selectable
      sortable
    >
      {renderTableHeader(columnSort, classes)}
      <Table.Body>{map(renderTableRow(classes), brandOffers)}</Table.Body>
    </Table>
  );
};

export default withStyles(styles)(OffersTable);
